import { useMutation } from "@tanstack/react-query";
import { useRefresh } from "ra-core";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { LeadSource } from "../types";

/**
 * PRD #16 §F — manual lead_source setter. mentorax.lead_snapshots is not
 * updatable through the contacts resource (it isn't a public.contacts
 * column, it lives in the mentorax schema) — same direct-client pattern as
 * useApprovalQueue.ts's useResolveApprovalItem. Upserts on contact_id so
 * this works whether or not Marco has ever written a snapshot row for this
 * contact yet (decision #5's read-model still gets a row either way).
 * RLS: mentorax.lead_snapshots' "Enable insert/update for crm users" policies
 * (is_crm_user()-gated) cover this write for any signed-in CRM user.
 */
export function useSetLeadSource() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({
      contactId,
      companyId,
      leadSource,
    }: {
      contactId: number;
      companyId?: number | null;
      leadSource: LeadSource | null;
    }) => {
      const { error } = await getSupabaseClient()
        .schema("mentorax")
        .from("lead_snapshots")
        .upsert(
          {
            contact_id: contactId,
            company_id: companyId ?? null,
            lead_source: leadSource,
          },
          { onConflict: "contact_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      // lead_snapshots lives outside react-admin's own dataProvider cache
      // (it's a direct client write, not an update("contacts", ...) call),
      // so refresh() is what pulls the new value back into the current view
      // — same pattern as BulkTagButton/DealShow's own out-of-band writes.
      refresh();
    },
  });
}
